import type { ComponentType } from "react";
import {
  IconBanknote2,
  IconConnectors1,
  IconConnectors2,
  IconCryptoWallet,
  IconFolderPaper,
  IconHandshake,
  IconHomeLine,
  IconLayoutDashboard,
  IconPageTextPieChart,
  IconPuzzle,
  IconRadio,
} from "@central-icons-react/square-outlined-radius-0-stroke-1.5";

import {
  IconDatabaseOutline18,
  IconDoubleChevronRightOutline18,
  IconHistoryOutline18,
  IconPlusOutline18,
} from "nucleo-ui-outline-18";

import { b3CoinIcon } from "@b3dotfun/sdk/shared/constants";
import { Address } from "thirdweb";

export const POLLING = {
  BLOCKS: 4_000, // Poll every 2 seconds for blocks
  STATS: 20_000, // Poll every 20 seconds for stats
  LONG_POLLING: 60_000, // Poll every 1 minute for long polling
} as const;

export const ANIMATION = {
  DURATION: 0.2, // Base duration for animations
  STAGGER: 0.05, // Stagger delay between items
  EXIT: {
    DURATION: 0.15,
    HEIGHT: 0.2,
  },
} as const;

export const SIDEBAR_WIDTH = "300px";

export const MARKETPLACE_CONTRACT_ADDRESS = process.env.NEXT_PUBLIC_MARKETPLACE_CONTRACT_ADDRESS as Address;

// B3 Token Contract
export const B3_CONTRACT_ADDRESS = "0xB3B32F9f8827D4634fE7d973Fa1034Ec9fdDB3B3" as Address;

// NFT Upgrade Contract
export const UPGRADE_CONTRACT_ADDRESS = "0x3999dBA122A2627a8eE393a350c2Da2C31104D14" as Address;

export const UPGRADE_PRODUCT_ID = "upgrade_spawn_nft";

// Upgrade Contract ABI
export const UPGRADE_CONTRACT_ABI = [
  {
    inputs: [
      {
        internalType: "string",
        name: "productId",
        type: "string",
      },
      {
        internalType: "string",
        name: "targetItemId",
        type: "string",
      },
    ],
    name: "purchaseProduct",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [
      {
        internalType: "string",
        name: "productId",
        type: "string",
      },
    ],
    name: "getProduct",
    outputs: [
      {
        components: [
          {
            internalType: "string",
            name: "id",
            type: "string",
          },
          {
            internalType: "string",
            name: "name",
            type: "string",
          },
          {
            internalType: "string",
            name: "description",
            type: "string",
          },
          {
            internalType: "string",
            name: "metadataUri",
            type: "string",
          },
          {
            internalType: "uint256",
            name: "price",
            type: "uint256",
          },
          {
            internalType: "address",
            name: "paymentToken",
            type: "address",
          },
          {
            internalType: "string",
            name: "actionId",
            type: "string",
          },
          {
            internalType: "string",
            name: "actionDescription",
            type: "string",
          },
          {
            internalType: "enum Marketplace.ProductStatus",
            name: "status",
            type: "uint8",
          },
          {
            internalType: "uint256",
            name: "totalSales",
            type: "uint256",
          },
          {
            internalType: "uint256",
            name: "createdAt",
            type: "uint256",
          },
          {
            internalType: "uint256",
            name: "updatedAt",
            type: "uint256",
          },
          {
            internalType: "bool",
            name: "requiresTarget",
            type: "bool",
          },
        ],
        internalType: "struct Marketplace.Product",
        name: "product",
        type: "tuple",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
] as const;

export const BASEMINT_REWARDS_RATES = {
  creator: 50,
  firstMinter: 20,
  gameOwner: 10,
  platform: 20,
} as const;

// both are feathers
export const BASEMENT_API_URL = "https://api.basement.fun";
export const B3_API_URL = process.env.NEXT_PUBLIC_B3_API || "https://api.b3.fun";

export const placeholderChainIcon = "https://cdn.b3.fun/b3-logo-white-circle.svg";

// Navigation links shared between MainSidebar, CommandMenu, and UserOrgSwitcher
export type NavigationGroup = "sidebar" | "workflow" | "plugins" | "account" | "org";

export interface NavigationLink {
  label: string;
  href: string;
  icon: ComponentType<{
    className?: string;
  }>;
  group: NavigationGroup;
  /** For expandable sidebar items, which sub-group they expand */
  expandsGroup?: NavigationGroup;
}

export const NAVIGATION_LINKS: NavigationLink[] = [
  // Sidebar main links — icon choices match Figma node 2564:9062 (B3-OS sidebar spec)
  {
    label: "Home",
    href: "/dashboard",
    icon: IconHomeLine,
    group: "sidebar",
  },
  {
    label: "Workflows",
    href: "/workflows",
    icon: IconLayoutDashboard,
    group: "sidebar",
    expandsGroup: "workflow",
  },
  {
    label: "Templates",
    href: "/templates",
    icon: IconFolderPaper,
    group: "sidebar",
  },
  {
    label: "Integrations",
    href: "/integrations",
    icon: IconConnectors1,
    group: "sidebar",
  },
  {
    label: "Connectors",
    href: "/connectors",
    icon: IconConnectors2,
    group: "sidebar",
  },
  {
    label: "Wallet Management",
    href: "/wallet-management",
    icon: IconCryptoWallet,
    group: "sidebar",
  },
  // Partner Program nav hidden until v2 launch — page still routable
  // at /partners directly, just not surfaced in the sidebar / ⌘K palette yet.
  // {
  //   label: "Partner Program",
  //   href: "/partners",
  //   icon: IconHandshake,
  //   group: "sidebar",
  // },
  {
    label: "Plugins",
    href: "/plugins",
    icon: IconPuzzle,
    group: "sidebar",
    expandsGroup: "plugins",
  },
  {
    label: "Billing",
    href: "/billing",
    icon: IconBanknote2,
    group: "sidebar",
  },
  // Plugins sub-links (shown in Plugins dropdown)
  {
    label: "Databases",
    href: "/databases",
    icon: IconDatabaseOutline18,
    group: "plugins",
  },
  // Workflow sub-links (shown in Workflows dropdown)
  {
    label: "All Workflows",
    href: "/workflows",
    icon: IconLayoutDashboard,
    group: "workflow",
  },
  {
    label: "Create new",
    href: "/workflows/edit",
    icon: IconPlusOutline18,
    group: "workflow",
  },
  {
    label: "Run History",
    href: "/workflows/history",
    icon: IconPageTextPieChart,
    group: "workflow",
  },
  {
    label: "Webhooks",
    href: "/webhooks",
    icon: IconRadio,
    group: "plugins",
  },

  // Account links (shown in user menu)
  {
    label: "Billing",
    href: "/billing",
    icon: IconBanknote2,
    group: "account",
  },
];

// Icon components for sidebar
export const SIDEBAR_ICONS = {
  IconHistoryOutline18,
  Collapse: IconDoubleChevronRightOutline18,
};

export const tokenIcons = {
  ETH: "https://cdn.b3.fun/ethereum.svg",
  WETH: "https://cdn.b3.fun/wrapped-ethereum.svg",
  USDC: "https://cdn.b3.fun/usd-coin.svg",
  USDT: "https://cdn.b3.fun/usdt.svg",
  DAI: "https://cdn.b3.fun/dai.svg",
  B3: b3CoinIcon,
  PRIME: "https://cdn.b3.fun/prime.png",
  TOSHI: "https://cdn.b3.fun/toshi-logo.svg",
  MOCHI: "https://cdn.b3.fun/mochi-logo.svg",
  // Add more asset icons as needed
};

export const avatarColors = ["#3368ef", "#272727", "#6366f1", "#06b6d4", "#eeb0d9", "#ba3fbf", "#ff777b", "#dfbb53"];

// Shared color palette for charts (pie charts, bar charts, etc.)
export const CHART_COLORS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // purple
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#2450FF", // blue
  "#84cc16", // lime
  "#6366f1", // indigo
  "#14b8a6", // teal
  "#eab308", // yellow
];

// Get color by index (cycles through palette)
export const getChartColor = (index: number): string => {
  return CHART_COLORS[index % CHART_COLORS.length];
};

// Generate a consistent color based on the user's address
export function generateColorFromAddress(address: string): string {
  const colors = avatarColors;

  // Use the address to generate a consistent index
  let hash = 0;
  for (let i = 0; i < address.length; i++) {
    hash = ((hash << 5) - hash + address.charCodeAt(i)) & 0xffffffff;
  }

  return colors[Math.abs(hash) % colors.length];
}
